import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import './App.css';
import useHomeData from './hooks/useHomeData';
import { HomeDataProvider } from './context/HomeDataContext';

const AboutSection = lazy(() => import('./components/AboutSection'));
const TechnologyShowcase = lazy(() => import('./components/TechnologyShowcase'));
const ServicesSection = lazy(() => import('./components/ServicesSection'));
const StatsSection = lazy(() => import('./components/StatsSection'));
const GovernmentServicesSection = lazy(() => import('./components/GovernmentServicesSection'));
const PeopleVerseSection = lazy(() => import('./components/PeopleVerseSection'));
const KnowledgeStreamSection = lazy(() => import('./components/KnowledgeStreamSection'));
const ContactSection = lazy(() => import('./components/ContactSection'));
const Footer = lazy(() => import('./components/Footer'));
const Toaster = lazy(() =>
  import('./components/ui/sonner').then((module) => ({ default: module.Toaster })),
);

function SectionFallback({ minHeight = 640 }) {
  return <div aria-hidden="true" style={{ minHeight }} />;
}

function LazySection({ children, minHeight, dataReady }) {
  const ref = useRef(null);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (shouldRender) {
      return undefined;
    }

    const node = ref.current;
    if (!node) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldRender(true);
          observer.disconnect();
        }
      },
      { rootMargin: '600px 0px' },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [shouldRender]);

  if (!shouldRender || !dataReady) {
    return <div ref={ref} aria-hidden="true" style={{ minHeight }} />;
  }

  return (
    <div ref={ref}>
      <Suspense fallback={<SectionFallback minHeight={minHeight} />}>
        {children}
      </Suspense>
    </div>
  );
}

function App() {
  const homeDataState = useHomeData();
  const dataReady = !homeDataState.isLoading && Boolean(homeDataState?.data?.story);

  return (
    <HomeDataProvider value={homeDataState}>
      <div className="App">
        <Navbar />
        <HeroSection />
        <LazySection minHeight={1200} dataReady={dataReady}>
          <AboutSection />
        </LazySection>
        <LazySection minHeight={900} dataReady={dataReady}>
          <TechnologyShowcase />
        </LazySection>
        <LazySection minHeight={500} dataReady={dataReady}>
          <StatsSection />
        </LazySection>
        <LazySection minHeight={900} dataReady={dataReady}>
          <ServicesSection />
        </LazySection>
        <LazySection minHeight={800} dataReady={dataReady}>
          <GovernmentServicesSection />
        </LazySection>
        <LazySection minHeight={900} dataReady={dataReady}>
          <PeopleVerseSection />
        </LazySection>
        <LazySection minHeight={900} dataReady={dataReady}>
          <KnowledgeStreamSection />
        </LazySection>
        <LazySection minHeight={700} dataReady={dataReady}>
          <ContactSection />
        </LazySection>
        <LazySection minHeight={520} dataReady={dataReady}>
          <Footer />
        </LazySection>
        <LazySection minHeight={1} dataReady={dataReady}>
          <Toaster />
        </LazySection>
      </div>
    </HomeDataProvider>
  );
}

export default App;